// fetchMessageTranslation.js
'use strict';
import throttle from 'lodash/throttle';

import fetch from '../resource/customFetch.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import handleFetchError from '../resource/handleFetchError.js';
import { localeConvertor } from '../resource/i18n.js';
import getMeData from '../selector/getMeData.js';
import getUserData from '../selector/getUserData.js';
import getMessageData from '../selector/getMessageData.js';
import getOperationData from '../selector/getOperationData.js';
import getNetworkingData from '../selector/getNetworkingData.js';
import addMessageTranslationThrottleId from '../action/addMessageTranslationThrottleId.js';
import resetMessageTranslationThrottleIds from '../action/resetMessageTranslationThrottleIds.js';
import {
  SET_NETWORKING_FETCHING_BATCH,
  SET_NETWORKING_SUCCESS_BATCH,
  SET_NETWORKING_ERROR_BATCH,
  ADD_MESSAGES,
  MERGE_USER,
} from '../ActionTypes.js';

const groupRegex = /(?<type>(message|user))-(?<id>[\S]+)/; // TODO: remote config

let throttleFunction = undefined;

/**
 * Fetch message translation
 * @kind action
 * @param {string} {messageId} - message id.
 * @param {string} {userId} - user id.
 * @param {string} {language} - language to fetch, will replace throttled requests.
 * @param {boolean} {shouldSkipCache} - should skip cache.
 * @return {Promise} Action promise.
 */
const fetchMessageTranslation =
  ({ messageId, userId, language: lang }) =>
  (dispatch, getState) => {
    dispatch(addMessageTranslationThrottleId({ messageId, userId }));

    const language = localeConvertor({
      locale: lang,
      isISO639: true,
    });
    const throttledGroups =
      getOperationData(getState(), ['throttledMessageTranslation'], 'groups') ||
      [];

    if (throttledGroups.length >= 10) {
      throttleFunction?.cancel();
      return dispatch(fetchMessageTranslationCore({ language }));
    }

    if (!throttleFunction) {
      throttleFunction = throttle(
        ({ language }) => dispatch(fetchMessageTranslationCore({ language })),
        1000, // TODO: remote config
        {
          leading: false,
        }
      );
    }

    return throttleFunction({ language });
  };

export const fetchMessageTranslationCore =
  ({ language = 'en', httpProxyHeaders = {}, shouldSkipCache = false } = {}) =>
  async (dispatch, getState) => {
    const meId = getMeData(getState(), 'id');
    const groups = getOperationData(
      getState(),
      ['throttledMessageTranslation'],
      'groups'
    );
    if (!groups?.length) {
      return dispatch({ type: '' });
    }

    dispatch(resetMessageTranslationThrottleIds());

    const filteredGroups = groups.filter(group => {
      const execResult = groupRegex.exec(group);
      if (!execResult) {
        return false;
      }
      const { type, id } = execResult.groups;
      const hasTranslation = !!(
        'message' === type
          ? getMessageData(getState(), id, 'hasTranslation')
          : getUserData(getState(), id, 'i18nBiography')
      )?.[language];
      return !hasTranslation;
    });

    if (!filteredGroups || filteredGroups.length === 0) {
      return { type: '' };
    }

    const unfetchedGroups = filteredGroups.filter(group => {
      const execResult = groupRegex.exec(group);
      if (!execResult) {
        return false;
      }
      const { id } = execResult.groups;
      const networkingSelectPath = ['translation', language, id];
      const isFetching = getNetworkingData(
        getState(),
        networkingSelectPath,
        'isFetching'
      );
      const isFetched = getNetworkingData(
        getState(),
        networkingSelectPath,
        'isFetched'
      );
      return !isFetching && !isFetched;
    });
    const unfetchedIds = unfetchedGroups.map(group => {
      const execResult = groupRegex.exec(group);
      const { id } = execResult.groups;
      return id;
    });

    if (!unfetchedIds || unfetchedIds.length === 0) {
      return { type: '' };
    }

    const fetchOptions = {
      method: 'GET',
      headers: {
        ...getHeaders(),
        ...httpProxyHeaders,
      },
    };

    const url = getResourceUrl({ endpoint: '/translations' });
    url.searchParams.set('lang', language);
    unfetchedGroups.forEach(group => {
      url.searchParams.append('group', group);
    });
    const TRANSLATION_API_VERSION_KEY = 'v';
    url.searchParams.set(TRANSLATION_API_VERSION_KEY, '2');

    const isIncludeMeId = unfetchedIds.includes(meId);
    if (isIncludeMeId || shouldSkipCache) {
      url.searchParams.append('_', Math.floor(Date.now() / 1000));
    }

    const networkingSelectPath = ['translation', language];

    dispatch({
      type: SET_NETWORKING_FETCHING_BATCH,
      payload: { selectPath: networkingSelectPath, items: unfetchedIds },
    });

    try {
      const response = await fetch(url.href, fetchOptions);

      if (!response.ok) {
        await handleFetchError({ response });
      }

      const data = await response.json();
      const lang = localeConvertor({
        locale: language,
        isISO639: false,
      });
      const messages = Object.create(null);
      Object.keys(data).map(key => {
        const { value, source_language, available_languages } = data[key] || {};
        const [group, id, i18nField] = key.split('-');
        if (!id || !group || !i18nField) {
          return;
        }
        if (group === 'message') {
          const payload = {
            id,
            source_language,
            available_languages,
          };
          if (i18nField === 'title') {
            payload.i18nTitle = {
              [lang]: value,
            };
          } else if (i18nField === 'caption') {
            payload.i18nCaption = {
              [lang]: value,
            };
          } else if (i18nField === 'description') {
            payload.i18nDescription = {
              [lang]: value,
            };
          } else if (i18nField === 'text') {
            payload.i18nText = {
              [lang]: value,
            };
          }
          if (messages[id]) {
            Object.assign(messages[id], payload);
          } else {
            messages[id] = payload;
          }
        } else if (group === 'user') {
          if (i18nField === 'biography') {
            dispatch({
              type: MERGE_USER,
              payload: {
                user: {
                  id,
                  source_language,
                  available_languages,
                  i18nBiography: {
                    [lang]: value,
                  },
                },
              },
            });
          }
        }
      });

      if (Object.keys(messages).length) {
        dispatch({
          type: ADD_MESSAGES,
          payload: { messages },
        });
      }

      return dispatch({
        type: SET_NETWORKING_SUCCESS_BATCH,
        payload: { selectPath: networkingSelectPath, items: unfetchedIds },
      });
    } catch (error) {
      return dispatch({
        type: SET_NETWORKING_ERROR_BATCH,
        payload: {
          selectPath: networkingSelectPath,
          items: unfetchedIds,
          error,
        },
      });
    }
  };

export default fetchMessageTranslation;
